import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import {
  Box,
  Card,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  IconButton,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import orgDataService from "services/orgDataService";
import vehiclesService from "services/vehiclesService";
import formsService from "services/formsService";
import { Hub } from "common/models";
import { RideAlongRequest } from "forms/models";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { Moment } from "moment";

type EvaluationRating = "satisfactory" | "coached" | "unsatisfactory" | null;

interface EvaluationItemProps {
  label: string;
  value: EvaluationRating;
  onChange: (value: EvaluationRating) => void;
  error?: boolean;
}

const EvaluationItem: React.FC<EvaluationItemProps> = ({ label, value, onChange, error }) => {
  return (
    <MDBox mb={2}>
      <MDTypography variant="body2" fontWeight="medium" mb={1}>
        {label}*
      </MDTypography>
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={(_, newValue) => onChange(newValue)}
        fullWidth
        sx={{
          "& .MuiToggleButton-root": {
            textTransform: "none",
            py: 1,
            border: error ? "1px solid #f44336" : undefined,
          },
          "& .Mui-selected": {
            backgroundColor: "info.dark",
            color: "white !important",
            "&:hover": {
              backgroundColor: "#01579b",
            },
          },
        }}
      >
        <ToggleButton value="satisfactory">Satisfactory</ToggleButton>
        <ToggleButton value="coached">Coached</ToggleButton>
        <ToggleButton value="unsatisfactory">Unsatisfactory</ToggleButton>
      </ToggleButtonGroup>
    </MDBox>
  );
};

interface VehicleOption {
  id: number;
  unitNumber: number;
}

interface PreTripEvaluation {
  performPreTripInspection: EvaluationRating;
  explainGauges: EvaluationRating;
  adjustMirrors: EvaluationRating;
  fastenSeatbelt: EvaluationRating;
  ensurePassengerSeatbelt: EvaluationRating;
  performGOAL: EvaluationRating;
  checkMirrorsBeforePullingOut: EvaluationRating;
}

interface VehicleOperationEvaluation {
  eyeLeadTime: EvaluationRating;
  aimHighInTurns: EvaluationRating;
  followingDistance: EvaluationRating;
  lookLeftRightLeft: EvaluationRating;
  paceTheLight: EvaluationRating;
  stopCarLengthBehind: EvaluationRating;
  completeStopAtControlled: EvaluationRating;
  checkMirrorsEvery5to8: EvaluationRating;
  laneOfLeastResistance: EvaluationRating;
  awareOfBlindAreas: EvaluationRating;
  adjustSpaceAndSpeed: EvaluationRating;
  turnSignalsWhenTurning: EvaluationRating;
  turnSignalsLaneChange: EvaluationRating;
  useSMOG: EvaluationRating;
  checkMirrorsPriorToSlowing: EvaluationRating;
  followRRCrossingPolicy: EvaluationRating;
}

interface BackingEvaluation {
  avoidBackingIfOptions: EvaluationRating;
  performGOAL: EvaluationRating;
  askForGuidance: EvaluationRating;
  checkAllMirrors: EvaluationRating;
  controlSpeedWhileBacking: EvaluationRating;
}

type TrainerDecision = "passed" | "failed" | null;

interface FormData {
  date: Moment | null;
  locationId: number | null;
  unitNumber: number | null;
  startOdometer: number | null;
  endOdometer: number | null;
  preTrip: PreTripEvaluation;
  vehicleOperation: VehicleOperationEvaluation;
  backing: BackingEvaluation;
  overallPerformance: EvaluationRating;
  trainerDecision: TrainerDecision;
  startTime: Moment | null;
  endTime: Moment | null;
  employeeName: string;
  employeeSignature: string;
  employeeSignatureDate: Moment | null;
  trainerName: string;
  trainerSignature: string;
  trainerSignatureDate: Moment | null;
}

interface FormErrors {
  date?: string;
  locationId?: string;
  unitNumber?: string;
  startOdometer?: string;
  endOdometer?: string;
  preTrip?: Partial<Record<keyof PreTripEvaluation, string>>;
  vehicleOperation?: Partial<Record<keyof VehicleOperationEvaluation, string>>;
  backing?: Partial<Record<keyof BackingEvaluation, string>>;
  overallPerformance?: string;
  trainerDecision?: string;
  startTime?: string;
  endTime?: string;
  employeeName?: string;
  employeeSignature?: string;
  employeeSignatureDate?: string;
  trainerName?: string;
  trainerSignature?: string;
  trainerSignatureDate?: string;
}

const preTripItems: { key: keyof PreTripEvaluation; label: string }[] = [
  { key: "performPreTripInspection", label: "Perform Pre-Trip Inspection" },
  { key: "explainGauges", label: "Explain Gauges on Instrument Panel" },
  { key: "adjustMirrors", label: "Adjust the mirrors properly" },
  { key: "fastenSeatbelt", label: "Fasten seatbelt" },
  { key: "ensurePassengerSeatbelt", label: "Ensure passenger's seatbelt was fastened" },
  { key: "performGOAL", label: "If backing, did driver perform G.O.A.L." },
  { key: "checkMirrorsBeforePullingOut", label: "Check mirrors and look over their left and right shoulder prior to pulling out" },
];

const vehicleOperationItems: { key: keyof VehicleOperationEvaluation; label: string }[] = [
  { key: "eyeLeadTime", label: "Maintain at least 15 sec. eye-lead time" },
  { key: "aimHighInTurns", label: "Aim high in turns/corners before committing the vehicle" },
  { key: "followingDistance", label: "Maintain at least 4 sec. following distance" },
  { key: "lookLeftRightLeft", label: "Look left/right/left before entering an intersection" },
  { key: "paceTheLight", label: "Pace the light" },
  { key: "stopCarLengthBehind", label: "Stop a car length behind vehicle at intersection" },
  { key: "completeStopAtControlled", label: "Come to a complete stop at controlled intersections" },
  { key: "checkMirrorsEvery5to8", label: "Check mirrors every 5-8 seconds" },
  { key: "laneOfLeastResistance", label: "Utilize the lane of least resistance" },
  { key: "awareOfBlindAreas", label: "Remain aware of blind areas / Remain aware of being in another's blind areas" },
  { key: "adjustSpaceAndSpeed", label: "Adjust space and speed to maintain a space cushion around the vehicle" },
  { key: "turnSignalsWhenTurning", label: "Use turn signals properly when turning" },
  { key: "turnSignalsLaneChange", label: "Use turn signals properly when making a lane change" },
  { key: "useSMOG", label: "Use S.M.O.G. when changing lanes (Signal, Mirrors, Over Shoulder, Go)" },
  { key: "checkMirrorsPriorToSlowing", label: "Check mirrors prior to slowing or stopping" },
  { key: "followRRCrossingPolicy", label: "Follow company policy when approaching/crossing RR crossings" },
];

const backingItems: { key: keyof BackingEvaluation; label: string }[] = [
  { key: "avoidBackingIfOptions", label: "Avoid backing if other options were available / Pull through or Turn around" },
  { key: "performGOAL", label: "Perform G.O.A.L" },
  { key: "askForGuidance", label: "Ask for guidance" },
  { key: "checkAllMirrors", label: "Check ALL Mirrors" },
  { key: "controlSpeedWhileBacking", label: "Control speed while backing" },
];

const initialPreTrip: PreTripEvaluation = {
  performPreTripInspection: null,
  explainGauges: null,
  adjustMirrors: null,
  fastenSeatbelt: null,
  ensurePassengerSeatbelt: null,
  performGOAL: null,
  checkMirrorsBeforePullingOut: null,
};

const initialVehicleOperation: VehicleOperationEvaluation = {
  eyeLeadTime: null,
  aimHighInTurns: null,
  followingDistance: null,
  lookLeftRightLeft: null,
  paceTheLight: null,
  stopCarLengthBehind: null,
  completeStopAtControlled: null,
  checkMirrorsEvery5to8: null,
  laneOfLeastResistance: null,
  awareOfBlindAreas: null,
  adjustSpaceAndSpeed: null,
  turnSignalsWhenTurning: null,
  turnSignalsLaneChange: null,
  useSMOG: null,
  checkMirrorsPriorToSlowing: null,
  followRRCrossingPolicy: null,
};

const initialBacking: BackingEvaluation = {
  avoidBackingIfOptions: null,
  performGOAL: null,
  askForGuidance: null,
  checkAllMirrors: null,
  controlSpeedWhileBacking: null,
};

const validationSchema = Yup.object().shape({
  date: Yup.date().required("Date is required").nullable(),
  locationId: Yup.number().required("Location is required").nullable(),
  unitNumber: Yup.number().required("Vehicle is required").nullable(),
});

const RideAlong = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState<Hub[]>([]);
  const [vehicles, setVehicles] = useState<VehicleOption[]>([]);
  const [formData, setFormData] = useState<FormData>({
    date: null,
    locationId: null,
    unitNumber: null,
    startOdometer: null,
    endOdometer: null,
    preTrip: initialPreTrip,
    vehicleOperation: initialVehicleOperation,
    backing: initialBacking,
    overallPerformance: null,
    trainerDecision: null,
    startTime: null,
    endTime: null,
    employeeName: "",
    employeeSignature: "",
    employeeSignatureDate: null,
    trainerName: "",
    trainerSignature: "",
    trainerSignatureDate: null,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const employeeSignatureRef = useRef<SignatureCanvas>(null);
  const trainerSignatureRef = useRef<SignatureCanvas>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (formData.locationId) {
      loadVehiclesByHub(formData.locationId);
    } else {
      setVehicles([]);
    }
  }, [formData.locationId]);

  const loadData = async () => {
    try {
      const hubsData = await orgDataService.getAllHubs();
      setLocations(hubsData);
    } catch (error) {
      toast.error("Failed to load form data");
    } finally {
      setIsLoading(false);
    }
  };

  const loadVehiclesByHub = async (hubId: number) => {
    setIsLoadingVehicles(true);
    try {
      const vehiclesData = await vehiclesService.getVehiclesByHub(hubId);
      setVehicles(
        vehiclesData.map((v: any) => ({
          id: v.id,
          unitNumber: v.unitNumber,
        }))
      );
    } catch (error) {
      toast.error("Failed to load vehicles");
      setVehicles([]);
    } finally {
      setIsLoadingVehicles(false);
    }
  };

  const validate = (): boolean => {
    let isValid = true;
    const newErrors: FormErrors = {};

    try {
      validationSchema.validateSync(formData, { abortEarly: false });
    } catch (error) {
      isValid = false;
      const validation = error as Yup.ValidationError;
      validation.inner.forEach((err) => {
        if (err.path) {
          newErrors[err.path as keyof FormErrors] = err.message;
        }
      });
    }

    // Validate pre-trip items
    const preTripErrors: Partial<Record<keyof PreTripEvaluation, string>> = {};
    preTripItems.forEach((item) => {
      if (!formData.preTrip[item.key]) {
        preTripErrors[item.key] = "Required";
        isValid = false;
      }
    });
    if (Object.keys(preTripErrors).length > 0) {
      newErrors.preTrip = preTripErrors;
    }

    // Validate vehicle operation items
    const vehicleOpErrors: Partial<Record<keyof VehicleOperationEvaluation, string>> = {};
    vehicleOperationItems.forEach((item) => {
      if (!formData.vehicleOperation[item.key]) {
        vehicleOpErrors[item.key] = "Required";
        isValid = false;
      }
    });
    if (Object.keys(vehicleOpErrors).length > 0) {
      newErrors.vehicleOperation = vehicleOpErrors;
    }

    // Validate backing items
    const backingErrors: Partial<Record<keyof BackingEvaluation, string>> = {};
    backingItems.forEach((item) => {
      if (!formData.backing[item.key]) {
        backingErrors[item.key] = "Required";
        isValid = false;
      }
    });
    if (Object.keys(backingErrors).length > 0) {
      newErrors.backing = backingErrors;
    }

    // Validate overall performance
    if (!formData.overallPerformance) {
      newErrors.overallPerformance = "Required";
      isValid = false;
    }

    // Validate odometer
    if (formData.startOdometer === null) {
      newErrors.startOdometer = "Start odometer is required";
      isValid = false;
    }
    if (formData.endOdometer === null) {
      newErrors.endOdometer = "End odometer is required";
      isValid = false;
    }

    // Validate summary fields
    if (!formData.trainerDecision) {
      newErrors.trainerDecision = "Required";
      isValid = false;
    }
    if (!formData.startTime) {
      newErrors.startTime = "Start time is required";
      isValid = false;
    }
    if (!formData.endTime) {
      newErrors.endTime = "End time is required";
      isValid = false;
    }
    if (!formData.employeeName) {
      newErrors.employeeName = "Employee name is required";
      isValid = false;
    }
    if (!formData.employeeSignature) {
      newErrors.employeeSignature = "Employee signature is required";
      isValid = false;
    }
    if (!formData.employeeSignatureDate) {
      newErrors.employeeSignatureDate = "Date is required";
      isValid = false;
    }
    if (!formData.trainerName) {
      newErrors.trainerName = "Trainer name is required";
      isValid = false;
    }
    if (!formData.trainerSignature) {
      newErrors.trainerSignature = "Trainer signature is required";
      isValid = false;
    }
    if (!formData.trainerSignatureDate) {
      newErrors.trainerSignatureDate = "Date is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handlePreTripChange = (key: keyof PreTripEvaluation, value: EvaluationRating) => {
    setFormData({
      ...formData,
      preTrip: { ...formData.preTrip, [key]: value },
    });
    if (errors.preTrip?.[key]) {
      setErrors({
        ...errors,
        preTrip: { ...errors.preTrip, [key]: undefined },
      });
    }
  };

  const handleVehicleOperationChange = (key: keyof VehicleOperationEvaluation, value: EvaluationRating) => {
    setFormData({
      ...formData,
      vehicleOperation: { ...formData.vehicleOperation, [key]: value },
    });
    if (errors.vehicleOperation?.[key]) {
      setErrors({
        ...errors,
        vehicleOperation: { ...errors.vehicleOperation, [key]: undefined },
      });
    }
  };

  const handleBackingChange = (key: keyof BackingEvaluation, value: EvaluationRating) => {
    setFormData({
      ...formData,
      backing: { ...formData.backing, [key]: value },
    });
    if (errors.backing?.[key]) {
      setErrors({
        ...errors,
        backing: { ...errors.backing, [key]: undefined },
      });
    }
  };

  const handleEmployeeSignatureEnd = () => {
    if (employeeSignatureRef.current) {
      const signatureData = employeeSignatureRef.current.toDataURL();
      setFormData({ ...formData, employeeSignature: signatureData });
      if (errors.employeeSignature) {
        setErrors({ ...errors, employeeSignature: undefined });
      }
    }
  };

  const handleTrainerSignatureEnd = () => {
    if (trainerSignatureRef.current) {
      const signatureData = trainerSignatureRef.current.toDataURL();
      setFormData({ ...formData, trainerSignature: signatureData });
      if (errors.trainerSignature) {
        setErrors({ ...errors, trainerSignature: undefined });
      }
    }
  };

  const clearEmployeeSignature = () => {
    employeeSignatureRef.current?.clear();
    setFormData({ ...formData, employeeSignature: "" });
  };

  const clearTrainerSignature = () => {
    trainerSignatureRef.current?.clear();
    setFormData({ ...formData, trainerSignature: "" });
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const selectedLocation = locations.find((l) => l.id === formData.locationId);

      const request: RideAlongRequest = {
        date: formData.date!.toISOString(),
        locationId: formData.locationId!,
        locationName: selectedLocation?.name || "",
        unitNumber: formData.unitNumber!,
        startOdometer: formData.startOdometer!,
        endOdometer: formData.endOdometer!,
        preTrip: {
          performPreTripInspection: formData.preTrip.performPreTripInspection || "",
          explainGauges: formData.preTrip.explainGauges || "",
          adjustMirrors: formData.preTrip.adjustMirrors || "",
          fastenSeatbelt: formData.preTrip.fastenSeatbelt || "",
          ensurePassengerSeatbelt: formData.preTrip.ensurePassengerSeatbelt || "",
          performGOAL: formData.preTrip.performGOAL || "",
          checkMirrorsBeforePullingOut: formData.preTrip.checkMirrorsBeforePullingOut || "",
        },
        vehicleOperation: {
          eyeLeadTime: formData.vehicleOperation.eyeLeadTime || "",
          aimHighInTurns: formData.vehicleOperation.aimHighInTurns || "",
          followingDistance: formData.vehicleOperation.followingDistance || "",
          lookLeftRightLeft: formData.vehicleOperation.lookLeftRightLeft || "",
          paceTheLight: formData.vehicleOperation.paceTheLight || "",
          stopCarLengthBehind: formData.vehicleOperation.stopCarLengthBehind || "",
          completeStopAtControlled: formData.vehicleOperation.completeStopAtControlled || "",
          checkMirrorsEvery5to8: formData.vehicleOperation.checkMirrorsEvery5to8 || "",
          laneOfLeastResistance: formData.vehicleOperation.laneOfLeastResistance || "",
          awareOfBlindAreas: formData.vehicleOperation.awareOfBlindAreas || "",
          adjustSpaceAndSpeed: formData.vehicleOperation.adjustSpaceAndSpeed || "",
          turnSignalsWhenTurning: formData.vehicleOperation.turnSignalsWhenTurning || "",
          turnSignalsLaneChange: formData.vehicleOperation.turnSignalsLaneChange || "",
          useSMOG: formData.vehicleOperation.useSMOG || "",
          checkMirrorsPriorToSlowing: formData.vehicleOperation.checkMirrorsPriorToSlowing || "",
          followRRCrossingPolicy: formData.vehicleOperation.followRRCrossingPolicy || "",
        },
        backing: {
          avoidBackingIfOptions: formData.backing.avoidBackingIfOptions || "",
          performGOAL: formData.backing.performGOAL || "",
          askForGuidance: formData.backing.askForGuidance || "",
          checkAllMirrors: formData.backing.checkAllMirrors || "",
          controlSpeedWhileBacking: formData.backing.controlSpeedWhileBacking || "",
        },
        overallPerformance: formData.overallPerformance || "",
        trainerDecision: formData.trainerDecision || "",
        startTime: formData.startTime!.toISOString(),
        endTime: formData.endTime!.toISOString(),
        employeeName: formData.employeeName,
        employeeSignature: formData.employeeSignature,
        employeeSignatureDate: formData.employeeSignatureDate!.toISOString(),
        trainerName: formData.trainerName,
        trainerSignature: formData.trainerSignature,
        trainerSignatureDate: formData.trainerSignatureDate!.toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      await formsService.submitRideAlong(request);
      toast.success("Ride Along form submitted successfully");
      navigate("/forms");
    } catch (error) {
      toast.error("Failed to submit Ride Along form");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof FormData, value: any) => {
    if (field === "locationId") {
      setFormData({ ...formData, [field]: value, unitNumber: null });
      setErrors({ ...errors, locationId: undefined, unitNumber: undefined });
    } else {
      setFormData({ ...formData, [field]: value });
      if (errors[field]) {
        setErrors({ ...errors, [field]: undefined });
      }
    }
  };

  if (isLoading) {
    return (
      <MDBox p={3}>
        <MDTypography>Loading...</MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <MDBox>
          <MDTypography variant="h5" fontWeight="medium">
            Ride Along
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Pre-employment ride along evaluation
          </MDTypography>
        </MDBox>
        <MDButton variant="outlined" color="secondary" onClick={() => navigate("/forms")}>
          Back to Forms
        </MDButton>
      </MDBox>

      <Card>
        <MDBox p={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Date *"
                value={formData.date}
                onChange={(value) => handleChange("date", value)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.date,
                    helperText: errors.date,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} />

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.locationId}>
                <InputLabel id="location-label">Location *</InputLabel>
                <Select
                  labelId="location-label"
                  id="locationId"
                  value={formData.locationId || ""}
                  label="Location *"
                  onChange={(e) => handleChange("locationId", e.target.value)}
                >
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.locationId && <FormHelperText>{errors.locationId}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.unitNumber} disabled={!formData.locationId || isLoadingVehicles}>
                <InputLabel id="vehicle-label">
                  {isLoadingVehicles ? "Loading vehicles..." : "Vehicle *"}
                </InputLabel>
                <Select
                  labelId="vehicle-label"
                  id="unitNumber"
                  value={formData.unitNumber || ""}
                  label={isLoadingVehicles ? "Loading vehicles..." : "Vehicle *"}
                  onChange={(e) => handleChange("unitNumber", e.target.value)}
                >
                  {vehicles.map((vehicle) => (
                    <MenuItem key={vehicle.unitNumber} value={vehicle.unitNumber}>
                      {vehicle.unitNumber}
                    </MenuItem>
                  ))}
                </Select>
                {errors.unitNumber && <FormHelperText>{errors.unitNumber}</FormHelperText>}
                {!formData.locationId && !errors.unitNumber && (
                  <FormHelperText>Select a location first</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Start Odometer *"
                type="number"
                value={formData.startOdometer ?? ""}
                onChange={(e) => handleChange("startOdometer", e.target.value ? Number(e.target.value) : null)}
                error={!!errors.startOdometer}
                helperText={errors.startOdometer}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="End Odometer *"
                type="number"
                value={formData.endOdometer ?? ""}
                onChange={(e) => handleChange("endOdometer", e.target.value ? Number(e.target.value) : null)}
                error={!!errors.endOdometer}
                helperText={errors.endOdometer}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Total Mileage"
                value={
                  formData.startOdometer !== null && formData.endOdometer !== null
                    ? formData.endOdometer - formData.startOdometer
                    : ""
                }
                InputProps={{ readOnly: true }}
                disabled
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          {/* Pre-Trip Evaluation Section */}
          <MDBox>
            <MDTypography variant="h6" fontWeight="medium" mb={3}>
              PRE-TRIP / DID DRIVER?
            </MDTypography>
            <Grid container spacing={2}>
              {preTripItems.map((item) => (
                <Grid item xs={12} md={6} key={item.key}>
                  <EvaluationItem
                    label={item.label}
                    value={formData.preTrip[item.key]}
                    onChange={(value) => handlePreTripChange(item.key, value)}
                    error={!!errors.preTrip?.[item.key]}
                  />
                </Grid>
              ))}
            </Grid>
          </MDBox>

          <Divider sx={{ my: 4 }} />

          {/* Vehicle Operation Section */}
          <MDBox>
            <MDTypography variant="h6" fontWeight="medium" mb={3}>
              VEHICLE OPERATION / DID DRIVER?
            </MDTypography>
            <Grid container spacing={2}>
              {vehicleOperationItems.map((item) => (
                <Grid item xs={12} md={6} key={item.key}>
                  <EvaluationItem
                    label={item.label}
                    value={formData.vehicleOperation[item.key]}
                    onChange={(value) => handleVehicleOperationChange(item.key, value)}
                    error={!!errors.vehicleOperation?.[item.key]}
                  />
                </Grid>
              ))}
            </Grid>
          </MDBox>

          <Divider sx={{ my: 4 }} />

          {/* Backing Section */}
          <MDBox>
            <MDTypography variant="h6" fontWeight="medium" mb={3}>
              BACKING / DID DRIVER?
            </MDTypography>
            <Grid container spacing={2}>
              {backingItems.map((item) => (
                <Grid item xs={12} md={6} key={item.key}>
                  <EvaluationItem
                    label={item.label}
                    value={formData.backing[item.key]}
                    onChange={(value) => handleBackingChange(item.key, value)}
                    error={!!errors.backing?.[item.key]}
                  />
                </Grid>
              ))}
            </Grid>
          </MDBox>

          <Divider sx={{ my: 4 }} />

          {/* Overall Driving Performance */}
          <MDBox>
            <EvaluationItem
              label="OVERALL DRIVING PERFORMANCE"
              value={formData.overallPerformance}
              onChange={(value) => {
                setFormData({ ...formData, overallPerformance: value });
                if (errors.overallPerformance) {
                  setErrors({ ...errors, overallPerformance: undefined });
                }
              }}
              error={!!errors.overallPerformance}
            />
          </MDBox>

          <Divider sx={{ my: 4 }} />

          {/* Summary Section */}
          <MDBox>
            <MDTypography variant="h6" fontWeight="medium" mb={3}>
              Summary
            </MDTypography>

            <MDBox mb={3}>
              <MDTypography variant="body2" fontWeight="medium" mb={1}>
                Trainer's Ride Along Decision*
              </MDTypography>
              <ToggleButtonGroup
                value={formData.trainerDecision}
                exclusive
                onChange={(_, value) => {
                  setFormData({ ...formData, trainerDecision: value });
                  if (errors.trainerDecision) {
                    setErrors({ ...errors, trainerDecision: undefined });
                  }
                }}
                fullWidth
                sx={{
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    py: 1,
                    border: errors.trainerDecision ? "1px solid #f44336" : undefined,
                  },
                  "& .Mui-selected": {
                    backgroundColor: "info.dark",
                    color: "white !important",
                    "&:hover": {
                      backgroundColor: "#01579b",
                    },
                  },
                }}
              >
                <ToggleButton value="passed">Passed</ToggleButton>
                <ToggleButton value="failed">Failed</ToggleButton>
              </ToggleButtonGroup>
            </MDBox>

            {/* Overall Time Section */}
            <MDBox mb={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Overall Time
              </MDTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <DateTimePicker
                    label="Start Time *"
                    value={formData.startTime}
                    onChange={(value) => handleChange("startTime", value)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.startTime,
                        helperText: errors.startTime,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DateTimePicker
                    label="End Time *"
                    value={formData.endTime}
                    onChange={(value) => handleChange("endTime", value)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.endTime,
                        helperText: errors.endTime,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Total Time"
                    value={
                      formData.startTime && formData.endTime
                        ? (() => {
                            const diff = formData.endTime.diff(formData.startTime, "minutes");
                            if (diff < 0) return "Invalid time range";
                            const hours = Math.floor(diff / 60);
                            const minutes = diff % 60;
                            return `${hours}h ${minutes}m`;
                          })()
                        : ""
                    }
                    InputProps={{ readOnly: true }}
                    disabled
                  />
                </Grid>
              </Grid>
            </MDBox>

            <MDTypography variant="body2" color="text" mb={3}>
              This is to certify that the above-named driver was given a road test under my supervision.
            </MDTypography>

            {/* Employee Name and Date Row */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Employee's Name *"
                  value={formData.employeeName}
                  onChange={(e) => handleChange("employeeName", e.target.value)}
                  error={!!errors.employeeName}
                  helperText={errors.employeeName}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Employee Date *"
                  value={formData.employeeSignatureDate}
                  onChange={(value) => handleChange("employeeSignatureDate", value)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.employeeSignatureDate,
                      helperText: errors.employeeSignatureDate,
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* Trainer Name and Date Row */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Trainer Name *"
                  value={formData.trainerName}
                  onChange={(e) => handleChange("trainerName", e.target.value)}
                  error={!!errors.trainerName}
                  helperText={errors.trainerName}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Trainer Date *"
                  value={formData.trainerSignatureDate}
                  onChange={(value) => handleChange("trainerSignatureDate", value)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.trainerSignatureDate,
                      helperText: errors.trainerSignatureDate,
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* Signatures Row */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <MDBox>
                  <MDTypography variant="caption" color="text">
                    Employee's Signature *
                  </MDTypography>
                  <MDBox
                    sx={{
                      border: errors.employeeSignature ? "1px solid #f44336" : "1px solid #d2d6da",
                      borderRadius: "8px",
                      position: "relative",
                      backgroundColor: "#fff",
                    }}
                  >
                    <SignatureCanvas
                      ref={employeeSignatureRef}
                      canvasProps={{
                        style: { width: "100%", height: "100px" },
                      }}
                      onEnd={handleEmployeeSignatureEnd}
                    />
                    <IconButton
                      size="small"
                      onClick={clearEmployeeSignature}
                      sx={{ position: "absolute", top: 2, right: 2 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </MDBox>
                  {errors.employeeSignature && (
                    <FormHelperText error>{errors.employeeSignature}</FormHelperText>
                  )}
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox>
                  <MDTypography variant="caption" color="text">
                    Trainer Signature *
                  </MDTypography>
                  <MDBox
                    sx={{
                      border: errors.trainerSignature ? "1px solid #f44336" : "1px solid #d2d6da",
                      borderRadius: "8px",
                      position: "relative",
                      backgroundColor: "#fff",
                    }}
                  >
                    <SignatureCanvas
                      ref={trainerSignatureRef}
                      canvasProps={{
                        style: { width: "100%", height: "100px" },
                      }}
                      onEnd={handleTrainerSignatureEnd}
                    />
                    <IconButton
                      size="small"
                      onClick={clearTrainerSignature}
                      sx={{ position: "absolute", top: 2, right: 2 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </MDBox>
                  {errors.trainerSignature && (
                    <FormHelperText error>{errors.trainerSignature}</FormHelperText>
                  )}
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4, gap: 2 }}>
            <MDButton variant="outlined" color="secondary" onClick={() => navigate("/forms")} disabled={isSubmitting}>
              Cancel
            </MDButton>
            <MDButton variant="contained" color="dark" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit"}
            </MDButton>
          </Box>
        </MDBox>
      </Card>
    </MDBox>
  );
};

export default RideAlong;
